import { useState } from "react";
import { toast } from "sonner";
import { MapPinned, Save, Move } from "lucide-react";
import { MapPicker } from "@/components/MapPicker";
import { inputCls } from "@/components/AuthLayout";
import { api, errMsg, fmtDate } from "@/lib/api";
import { usePoll } from "@/lib/usePoll";

const STATE = (d) =>
  !d.is_online ? ["Offline", "bg-slate-100 text-slate-500"]
    : !d.live ? ["Online · no GPS", "bg-red-50 text-red-600"]
      : d.trip_status ? [d.trip_status === "in_progress" ? "On trip" : "Heading to pickup", "bg-blue-50 text-blue-700"]
        : ["Available", "bg-emerald-50 text-emerald-700"];

const BaseEditor = ({ base, draft, setDraft, moving, setMoving, onSaved }) => {
  const cur = draft || base;
  const save = async () => {
    try {
      await api.put("/fleet/base", { name: cur.name, lat: cur.lat, lng: cur.lng, radius_m: Number(cur.radius_m) });
      toast.success("Base location saved");
      setDraft(null);
      setMoving(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!cur) return null;
  return (
    <div data-testid="base-editor" className="space-y-2 rounded-2xl border border-blue-100 bg-white p-4">
      <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Taxi base / pool</p>
      <input data-testid="base-name-input" className={inputCls} value={cur.name} onChange={(e) => setDraft({ ...cur, name: e.target.value })} />
      <label className="flex items-center gap-2 text-xs text-slate-500">Return radius (m)
        <input data-testid="base-radius-input" type="number" min="50" max="2000" className="h-9 w-24 rounded-lg border border-slate-200 px-2 text-sm" value={cur.radius_m} onChange={(e) => setDraft({ ...cur, radius_m: e.target.value })} />
      </label>
      <p className="font-mono-num text-xs text-slate-400">{cur.lat.toFixed(5)}, {cur.lng.toFixed(5)}</p>
      <div className="flex gap-2">
        <button data-testid="move-base-button" onClick={() => setMoving(!moving)} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${moving ? "bg-amber-400 text-slate-900" : "bg-slate-100 text-slate-700"}`}><Move className="h-3.5 w-3.5" />{moving ? "Tap map to place base" : "Move base pin"}</button>
        {draft && <button data-testid="save-base-button" onClick={save} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white"><Save className="h-3.5 w-3.5" />Save</button>}
      </div>
    </div>
  );
};

export const LiveMapPanel = () => {
  const [data, setData] = useState(null);
  const [focus, setFocus] = useState(null);
  const [draft, setDraft] = useState(null);
  const [moving, setMoving] = useState(false);
  const load = () => api.get("/fleet/live").then((r) => setData(r.data)).catch(() => {});
  usePoll(load, 8000, []);
  if (!data) return <div className="h-96 animate-pulse rounded-2xl bg-white" />;
  const located = data.drivers.filter((d) => d.lat != null);
  const base = draft || data.base;
  const fitPoints = focus ? [[focus.lat, focus.lng]] : [...located.map((d) => [d.lat, d.lng]), [base.lat, base.lng]];
  return (
    <div className="grid gap-6 lg:grid-cols-12">
      <div className="lg:col-span-8">
        <MapPicker
          testId="fleet-live-map"
          drivers={located}
          base={base}
          onDriverClick={setFocus}
          selectedDriverId={focus?.driver_id}
          onPick={moving ? (lat, lng) => setDraft({ ...base, lat, lng }) : undefined}
          fitPoints={fitPoints}
          fitKey={`${focus?.driver_id || "all"}-${located.length}-${draft ? "d" : ""}`}
          className="h-[420px] sm:h-[600px]"
        />
        <p className="mt-2 flex items-center gap-2 text-xs text-slate-500"><MapPinned className="h-3.5 w-3.5" /> Yellow = live, grey = last known position. Refreshes every 8 seconds.</p>
      </div>
      <div className="space-y-4 lg:col-span-4">
        <BaseEditor base={data.base} draft={draft} setDraft={setDraft} moving={moving} setMoving={setMoving} onSaved={load} />
        <div className="rounded-2xl border border-blue-100 bg-white p-4" data-testid="fleet-live-driver-list">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Drivers ({data.drivers.length})</p>
            {focus && <button onClick={() => setFocus(null)} className="text-xs font-semibold text-blue-700">Show all</button>}
          </div>
          <div className="max-h-[360px] space-y-1 overflow-y-auto">
            {data.drivers.map((d) => {
              const [label, cls] = STATE(d);
              return (
                <button key={d.driver_id} data-testid={`live-driver-${d.driver_id}`} disabled={d.lat == null} onClick={() => setFocus(d)} className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-slate-50 disabled:opacity-60">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-900">{d.taxi_no ? `#${d.taxi_no} · ` : ""}{d.name}</span>
                    <span className="block text-[11px] text-slate-400">{d.updated_at ? `seen ${fmtDate(d.updated_at)}` : "no location yet"}</span>
                  </span>
                  <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{label}</span>
                </button>
              );
            })}
          </div>
        </div>
        {data.waiting.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="fleet-waiting-list">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-700">Waiting for a taxi ({data.waiting.length})</p>
            {data.waiting.map((w) => <p key={w.id} className="mt-1 truncate text-sm text-slate-700">{w.customer} · {w.pickup.address}</p>)}
          </div>
        )}
      </div>
    </div>
  );
};
